// JSON decoding (and encoding) of everything TrailBivvy ships, into the src/core/models.ts types.
//
// Ports the `Codable` conformances of CoreModels (Trail.swift, TrailCatalog.swift, BivouacTrip.swift,
// PointOfInterest.swift, ParkingAttributes.swift, Regulation.swift, Bivouac.swift, DataProvenance.swift,
// GeometryCoding.swift, TrailDataset.swift) with the date strategy of CoreModels/Support/JSONCoding.swift, including
// the hand-written lenient decoders:
// - DataProvenance: `isSynthetic` defaults to false, `licenseReviewStatus` to `pendingHumanReview`;
// - CountryCode: upper-cased;
// - TransitStop: `lines` defaults to [];
// - ParkingAttributes: an unknown `kind` reads as absent (`try?`);
// - BivouacTripInfo: `liftFree` false, `nights` 1, empty lists, `rulesState` unknown and `rulesConfidence` absent when
//   missing or undecodable.
// Trail packs: compact POIs (`id n c la lo e cc a p cf`) are expanded with the pack's provenance table exactly like
// Swift `TrailPack.dataset()`; geometry uses the compact polyline codec of src/core/polyline.ts.
//
// Encoding mirrors Swift `JSONCoding.makeEncoder()`: ISO 8601 dates, enums with associated values as
// `{"case": {"_0": …}}`, verbose geometry unless `compactGeometry` is set. `encodeTrailDataset` round-trips with
// `decodeTrailDataset` (persisting user imports as JSON); a structured clone of the decoded objects works as well.

import {
  type PolygonJSON,
  PolylineCodecError,
  decodePolygon,
  decodePolyline,
  encodePolygon,
  encodePolyline,
} from '../core/polyline';
import type {
  AccessRule,
  AccommodationAttributes,
  BivouacArea,
  BivouacTripInfo,
  Confidence,
  CountryCode,
  DataProvenance,
  DatasetMetadata,
  DayRange,
  DifficultyGrade,
  ExternalReference,
  FoodAttributes,
  GeoBounds,
  GeoCoordinate,
  GeoPolygon,
  GeoPolyline,
  Jurisdiction,
  JurisdictionLevel,
  KeyPointKind,
  LandCover,
  LayerCheckResult,
  LicenseReviewStatus,
  LocalRuleNote,
  OfficialLayerCheck,
  OvernightRule,
  POIAttributes,
  POICategory,
  ParkingAttributes,
  ParkingKind,
  PermitKind,
  PermitRequirement,
  PlaceReference,
  PointOfInterest,
  ProtectedArea,
  ProtectedAreaKind,
  Regulation,
  RegulationCondition,
  RegulationEffect,
  RegulationScope,
  RegulationSource,
  RegulatoryCoverage,
  RegulatoryState,
  RoutePoint,
  SACScale,
  SeasonalWindow,
  SegmentHazard,
  StageDataQuality,
  StageKeyPoint,
  SurfaceType,
  TerrainGrid,
  TerrainHazard,
  TerrainSample,
  Trail,
  TrailCatalogEntry,
  TrailCatalogIndex,
  TrailCatalogRegion,
  TrailCategory,
  TrailClassification,
  TrailDataset,
  TrailKind,
  TrailPack,
  TrailRegion,
  TrailSegment,
  TrailStage,
  TransitAttributes,
  TransitStop,
  TripAccess,
  WaterAttributes,
  WindExposure,
} from '../core/models';
import {
  type Codec,
  DecodingError,
  type EncodeOptions,
  arrayCodec,
  boolCodec,
  dateCodec,
  defaulted,
  dictionaryCodec,
  doubleCodec,
  enumCodec,
  expectObject,
  intCodec,
  lenientDefaulted,
  lenientOptional,
  optional,
  required,
  rethrowAt,
  stringCodec,
  structCodec,
  taggedEnumCodec,
  urlCodec,
} from './codec';

export { DecodingError } from './codec';
export type { Codec, EncodeOptions } from './codec';

// MARK: - Enum raw values (Records so the compiler checks completeness against models.ts)

const t = true as const;

export const trailKindCodec = enumCodec<TrailKind>('TrailKind', { viaAlpina: t, imported: t, custom: t, catalog: t });
export const difficultyGradeCodec = enumCodec<DifficultyGrade>('DifficultyGrade', {
  easy: t,
  moderate: t,
  demanding: t,
  difficult: t,
});
export const sacScaleCodec = enumCodec<SACScale>('SACScale', { T1: t, T2: t, T3: t, T4: t, T5: t, T6: t });
export const trailClassificationCodec = enumCodec<TrailClassification>('TrailClassification', {
  hikingTrail: t,
  mountainHikingTrail: t,
  alpineHikingTrail: t,
  road: t,
  unmarked: t,
  unknown: t,
});
export const surfaceTypeCodec = enumCodec<SurfaceType>('SurfaceType', {
  paved: t,
  gravel: t,
  dirtPath: t,
  alpineMeadow: t,
  rocky: t,
  scree: t,
  snow: t,
  unknown: t,
});
export const segmentHazardCodec = enumCodec<SegmentHazard>('SegmentHazard', {
  exposure: t,
  scree: t,
  lingeringSnow: t,
  fixedRopesOrChains: t,
  ladders: t,
  streamCrossing: t,
  rockfall: t,
  livestock: t,
  steepDescent: t,
});
export const keyPointKindCodec = enumCodec<KeyPointKind>('KeyPointKind', {
  pass: t,
  summit: t,
  settlement: t,
  hut: t,
  junction: t,
  water: t,
  transit: t,
  viewpoint: t,
  campsite: t,
  shelter: t,
  bivouacArea: t,
});
export const stageDataQualityCodec = enumCodec<StageDataQuality>('StageDataQuality', {
  demoSynthetic: t,
  approximate: t,
  authoritative: t,
  userImported: t,
  openData: t,
});
export const trailCategoryCodec = enumCodec<TrailCategory>('TrailCategory', {
  thruHike: t,
  longDistance: t,
  hutTrek: t,
  alpineTraverse: t,
  coastal: t,
  pilgrimage: t,
  viaFerrata: t,
  bivouacTrip: t,
});
export const trailCatalogRegionCodec = enumCodec<TrailCatalogRegion>('TrailCatalogRegion', {
  alps: t,
  pyrenees: t,
  iberia: t,
  mediterranean: t,
  britishIsles: t,
  nordic: t,
  centralEurope: t,
  balkans: t,
  usPacific: t,
  usRockies: t,
  usSouthwest: t,
  usEast: t,
  usMidwest: t,
});
export const permitKindCodec = enumCodec<PermitKind>('PermitRequirement.Kind', {
  required: t,
  quota: t,
  reservation: t,
  registration: t,
  fee: t,
  recommended: t,
});
export const confidenceCodec = enumCodec<Confidence>('Confidence', { low: t, medium: t, high: t });
export const licenseReviewStatusCodec = enumCodec<LicenseReviewStatus>('LicenseReviewStatus', {
  verified: t,
  pendingHumanReview: t,
  syntheticNoLicenceNeeded: t,
  userProvided: t,
});
export const poiCategoryCodec = enumCodec<POICategory>('POICategory', {
  camping: t,
  bivouacHut: t,
  mountainHut: t,
  shelter: t,
  hotelHostel: t,
  village: t,
  water: t,
  foodShop: t,
  restaurant: t,
  pharmacy: t,
  doctor: t,
  train: t,
  bus: t,
  cableCar: t,
  emergency: t,
  toilet: t,
  laundry: t,
  equipmentShop: t,
  charging: t,
  roadAccess: t,
  parking: t,
});
const accommodationKindCodec = enumCodec<AccommodationAttributes['kind']>('AccommodationAttributes.Kind', {
  campsite: t,
  mountainHut: t,
  bivouacHut: t,
  shelter: t,
  hotel: t,
  hostel: t,
  guesthouse: t,
  dormitory: t,
});
const potabilityCodec = enumCodec<WaterAttributes['potability']>('WaterAttributes.Potability', {
  potable: t,
  treatBeforeDrinking: t,
  unknown: t,
});
const reliabilityCodec = enumCodec<WaterAttributes['reliability']>('WaterAttributes.Reliability', {
  permanent: t,
  seasonal: t,
  unreliable: t,
  unknown: t,
});
const foodKindCodec = enumCodec<FoodAttributes['kind']>('FoodAttributes.Kind', {
  grocery: t,
  bakery: t,
  restaurant: t,
  hutMeals: t,
  kiosk: t,
});
const transitModeCodec = enumCodec<TransitAttributes['mode']>('TransitAttributes.Mode', {
  train: t,
  bus: t,
  cableCar: t,
  funicular: t,
  boat: t,
});
export const parkingKindCodec = enumCodec<ParkingKind>('ParkingAttributes.Kind', {
  surface: t,
  'multi-storey': t,
  underground: t,
  street_side: t,
  lane: t,
  layby: t,
  rooftop: t,
});
export const regulatoryStateCodec = enumCodec<RegulatoryState>('RegulatoryState', {
  official: t,
  noKnownRestriction: t,
  caution: t,
  restricted: t,
  prohibited: t,
  unknown: t,
});
export const jurisdictionLevelCodec = enumCodec<JurisdictionLevel>('JurisdictionLevel', {
  international: t,
  federal: t,
  cantonal: t,
  municipal: t,
  protectedAreaAuthority: t,
  landowner: t,
});
export const regulationEffectCodec = enumCodec<RegulationEffect>('RegulationEffect', {
  prohibitsOvernight: t,
  permitsOnlyAtDesignatedSites: t,
  requiresPermission: t,
  toleratedWithConditions: t,
  advisory: t,
  noSpecificRule: t,
});
export const protectedAreaKindCodec = enumCodec<ProtectedAreaKind>('ProtectedAreaKind', {
  nationalPark: t,
  natureReserve: t,
  wildlifeRestZone: t,
  wildlifeReserve: t,
  moorland: t,
  floodplain: t,
  dryGrassland: t,
  landscapeInventory: t,
  unescoSite: t,
  other: t,
});
export const overnightRuleCodec = enumCodec<OvernightRule>('OvernightRule', {
  prohibited: t,
  restricted: t,
  allowedAtDesignatedSites: t,
  noRuleKnown: t,
  unknown: t,
});
export const accessRuleCodec = enumCodec<AccessRule>('AccessRule', {
  none: t,
  stayOnMarkedTrails: t,
  noAccess: t,
  unknown: t,
});
export const landCoverCodec = enumCodec<LandCover>('LandCover', {
  meadow: t,
  pasture: t,
  forest: t,
  openForest: t,
  shrub: t,
  rock: t,
  scree: t,
  glacier: t,
  wetland: t,
  water: t,
  settlement: t,
  cultivated: t,
  unknown: t,
});
export const windExposureCodec = enumCodec<WindExposure>('WindExposure', {
  sheltered: t,
  moderate: t,
  exposed: t,
  unknown: t,
});
export const terrainHazardCodec = enumCodec<TerrainHazard>('TerrainHazard', {
  avalanche: t,
  rockfall: t,
  flooding: t,
  glacier: t,
  cliffEdge: t,
  debrisFlow: t,
  lightningExposure: t,
});
export const layerCheckResultCodec = enumCodec<LayerCheckResult>('LayerCheckResult', { none: t, nearby: t, inside: t });

// MARK: - Geo primitives

/** `CountryCode`: a string, upper-cased on decode (Swift `CountryCode.init(rawValue:)`). */
export const countryCodeCodec: Codec<CountryCode> = {
  decode: (v) => stringCodec.decode(v).toUpperCase(),
  encode: (v) => v,
};

export const geoCoordinateCodec = structCodec<GeoCoordinate>({
  latitude: required(doubleCodec),
  longitude: required(doubleCodec),
});

export const routePointCodec = structCodec<RoutePoint>({
  coordinate: required(geoCoordinateCodec),
  elevation: optional(doubleCodec),
});

export const geoBoundsCodec = structCodec<GeoBounds>({
  minLatitude: required(doubleCodec),
  minLongitude: required(doubleCodec),
  maxLatitude: required(doubleCodec),
  maxLongitude: required(doubleCodec),
});

function codecFailure(e: unknown): never {
  if (e instanceof PolylineCodecError) {
    throw new DecodingError('dataCorrupted', `invalid compact geometry (${e.message})`);
  }
  throw e;
}

const verbosePolylineCodec = structCodec<GeoPolyline>({ points: required(arrayCodec(routePointCodec)) });
const compactPolylineCodec = structCodec<{ enc: string; ele?: string; p?: number }>({
  enc: required(stringCodec),
  ele: optional(stringCodec),
  p: optional(intCodec),
});
const verbosePolygonCodec = structCodec<{ exterior: GeoCoordinate[]; holes?: GeoCoordinate[][] }>({
  exterior: required(arrayCodec(geoCoordinateCodec)),
  holes: optional(arrayCodec(arrayCodec(geoCoordinateCodec))),
});
const compactPolygonCodec = structCodec<{ enc: string; encHoles?: string[]; p?: number }>({
  enc: required(stringCodec),
  encHoles: optional(arrayCodec(stringCodec)),
  p: optional(intCodec),
});

/** `GeoPolyline` (GeometryCoding.swift): verbose `points`, or compact `enc` (+ `ele` decimetres, `p`). */
export const geoPolylineCodec: Codec<GeoPolyline> = {
  decode(v) {
    const o = expectObject(v, 'GeoPolyline');
    if (Object.hasOwn(o, 'points')) return verbosePolylineCodec.decode(o);
    const compact = compactPolylineCodec.decode(o);
    try {
      return decodePolyline(compact);
    } catch (e) {
      return codecFailure(e);
    }
  },
  encode: (v, options) => encodePolyline(v, options.compactGeometry === true),
};

/** `GeoPolygon` (GeometryCoding.swift): `exterior` (+ `holes`), or compact `enc` (+ `encHoles`, `p`). */
export const geoPolygonCodec: Codec<GeoPolygon> = {
  decode(v) {
    const o = expectObject(v, 'GeoPolygon');
    const json: PolygonJSON = Object.hasOwn(o, 'exterior') ? verbosePolygonCodec.decode(o) : compactPolygonCodec.decode(o);
    try {
      return decodePolygon(json);
    } catch (e) {
      return codecFailure(e);
    }
  },
  encode: (v, options) => encodePolygon(v, options.compactGeometry === true),
};

const polygonsCodec = arrayCodec(geoPolygonCodec);
const stringsCodec = arrayCodec(stringCodec);

// MARK: - Provenance

export const dataProvenanceCodec = structCodec<DataProvenance>({
  provider: required(stringCodec),
  dataset: required(stringCodec),
  sourceURL: optional(urlCodec),
  license: optional(stringCodec),
  attribution: optional(stringCodec),
  fetchedAt: required(dateCodec),
  sourceUpdatedAt: optional(dateCodec),
  isSynthetic: defaulted(boolCodec, () => false),
  licenseReviewStatus: defaulted(licenseReviewStatusCodec, () => 'pendingHumanReview'),
});

// MARK: - Trails and stages

export const externalReferenceCodec = structCodec<ExternalReference>({
  title: required(stringCodec),
  url: optional(urlCodec),
});

export const trailRegionCodec = structCodec<TrailRegion>({
  id: required(stringCodec),
  name: required(stringCodec),
  countryCode: required(countryCodeCodec),
  stageIDs: required(stringsCodec),
  bounds: optional(geoBoundsCodec),
});

export const trailCodec = structCodec<Trail>({
  id: required(stringCodec),
  name: required(stringCodec),
  kind: required(trailKindCodec),
  summary: optional(stringCodec),
  countries: required(arrayCodec(countryCodeCodec)),
  regions: required(arrayCodec(trailRegionCodec)),
  stageIDs: required(stringsCodec),
  historicalNames: required(stringsCodec),
  officialReference: optional(externalReferenceCodec),
  provenance: required(dataProvenanceCodec),
});

export const placeReferenceCodec = structCodec<PlaceReference>({
  name: required(stringCodec),
  coordinate: required(geoCoordinateCodec),
  elevation: optional(doubleCodec),
  poiID: optional(stringCodec),
});

export const trailSegmentCodec = structCodec<TrailSegment>({
  id: required(stringCodec),
  stageID: required(stringCodec),
  index: required(intCodec),
  startDistance: required(doubleCodec),
  endDistance: required(doubleCodec),
  classification: required(trailClassificationCodec),
  surface: required(surfaceTypeCodec),
  sacScale: optional(sacScaleCodec),
  hazards: required(arrayCodec(segmentHazardCodec)),
});

export const stageKeyPointCodec = structCodec<StageKeyPoint>({
  id: required(stringCodec),
  name: required(stringCodec),
  kind: required(keyPointKindCodec),
  distanceFromStart: required(doubleCodec),
  coordinate: required(geoCoordinateCodec),
  elevation: optional(doubleCodec),
  poiID: optional(stringCodec),
});

export const trailStageCodec = structCodec<TrailStage>({
  id: required(stringCodec),
  trailID: required(stringCodec),
  number: required(intCodec),
  code: required(stringCodec),
  name: required(stringCodec),
  start: required(placeReferenceCodec),
  finish: required(placeReferenceCodec),
  countryCode: required(countryCodeCodec),
  regionID: optional(stringCodec),
  polyline: required(geoPolylineCodec),
  distance: required(doubleCodec),
  ascent: required(doubleCodec),
  descent: required(doubleCodec),
  minElevation: required(doubleCodec),
  maxElevation: required(doubleCodec),
  expectedDuration: required(doubleCodec),
  difficulty: required(difficultyGradeCodec),
  sacScale: optional(sacScaleCodec),
  classifications: required(arrayCodec(trailClassificationCodec)),
  surfaces: required(arrayCodec(surfaceTypeCodec)),
  segments: required(arrayCodec(trailSegmentCodec)),
  keyPoints: required(arrayCodec(stageKeyPointCodec)),
  waterPOIIDs: required(stringsCodec),
  settlementPOIIDs: required(stringsCodec),
  accommodationPOIIDs: required(stringsCodec),
  bailoutPOIIDs: required(stringsCodec),
  notes: required(stringsCodec),
  dataQuality: required(stageDataQualityCodec),
  provenance: required(dataProvenanceCodec),
});

// MARK: - Points of interest

export const seasonalWindowCodec = structCodec<SeasonalWindow>({
  startMonth: required(intCodec),
  startDay: required(intCodec),
  endMonth: required(intCodec),
  endDay: required(intCodec),
});

export const parkingAttributesCodec = structCodec<ParkingAttributes>({
  fee: optional(boolCodec),
  access: optional(stringCodec),
  maxStay: optional(stringCodec),
  openingHours: optional(stringCodec),
  kind: lenientOptional(parkingKindCodec),
  capacity: optional(intCodec),
  parkAndRide: optional(boolCodec),
  conditions: optional(stringsCodec),
});

export const poiAttributesCodec = structCodec<POIAttributes>({
  season: optional(seasonalWindowCodec),
  isYearRound: optional(boolCodec),
  capacity: optional(intCodec),
  isStaffed: optional(boolCodec),
  website: optional(urlCodec),
  phone: optional(stringCodec),
  operatorName: optional(stringCodec),
  reservationRecommended: optional(boolCodec),
  accommodation: optional(
    structCodec<AccommodationAttributes>({
      kind: required(accommodationKindCodec),
      allowsTents: optional(boolCodec),
      hasDormitory: optional(boolCodec),
      servesMeals: optional(boolCodec),
    }),
  ),
  water: optional(
    structCodec<WaterAttributes>({ potability: required(potabilityCodec), reliability: required(reliabilityCodec) }),
  ),
  food: optional(structCodec<FoodAttributes>({ kind: required(foodKindCodec), openingNote: optional(stringCodec) })),
  transit: optional(
    structCodec<TransitAttributes>({
      mode: required(transitModeCodec),
      stopIdentifier: optional(stringCodec),
      isSeasonal: optional(boolCodec),
      connectsTo: optional(stringCodec),
    }),
  ),
  chargingAvailable: optional(boolCodec),
  note: optional(stringCodec),
  parking: optional(parkingAttributesCodec),
});

export const pointOfInterestCodec = structCodec<PointOfInterest>({
  id: required(stringCodec),
  name: required(stringCodec),
  category: required(poiCategoryCodec),
  coordinate: required(geoCoordinateCodec),
  elevation: optional(doubleCodec),
  countryCode: optional(countryCodeCodec),
  attributes: required(poiAttributesCodec),
  provenance: required(dataProvenanceCodec),
  confidence: required(confidenceCodec),
  lastVerifiedAt: optional(dateCodec),
});

/** Compact POI record of a trail pack (Swift `CompactPOI`); `p` indexes the pack's provenance table. */
export interface CompactPOI {
  id: string;
  n: string;
  c: POICategory;
  la: number;
  lo: number;
  e?: number;
  cc?: CountryCode;
  a?: POIAttributes;
  p: number;
  cf: Confidence;
}

export const compactPOICodec = structCodec<CompactPOI>({
  id: required(stringCodec),
  n: required(stringCodec),
  c: required(poiCategoryCodec),
  la: required(doubleCodec),
  lo: required(doubleCodec),
  e: optional(doubleCodec),
  cc: optional(countryCodeCodec),
  a: optional(poiAttributesCodec),
  p: required(intCodec),
  cf: required(confidenceCodec),
});

// MARK: - Regulation

export const jurisdictionCodec = structCodec<Jurisdiction>({
  level: required(jurisdictionLevelCodec),
  countryCode: required(countryCodeCodec),
  name: required(stringCodec),
  code: optional(stringCodec),
});

export const regulationConditionCodec = taggedEnumCodec<RegulationCondition>('RegulationCondition', {
  singleNightOnly: {},
  aboveTreelineOnly: {},
  minimumDistanceFromSettlement: { meters: ['meters', doubleCodec] },
  minimumDistanceFromRoad: { meters: ['meters', doubleCodec] },
  maximumGroupSize: { count: ['_0', intCodec] },
  arriveLateLeaveEarly: {},
  noFires: {},
  landownerPermission: {},
  stayOnMarkedTrails: {},
  custom: { text: ['_0', stringCodec] },
});

export const regulationScopeCodec = taggedEnumCodec<RegulationScope>('RegulationScope', {
  country: { countryCode: ['_0', countryCodeCodec] },
  administrativeArea: { code: ['code', stringCodec], polygons: ['polygons', polygonsCodec] },
  area: { polygons: ['polygons', polygonsCodec] },
  protectedArea: { id: ['id', stringCodec] },
});

export const regulationSourceCodec = structCodec<RegulationSource>({
  name: required(stringCodec),
  identifier: required(stringCodec),
  jurisdiction: required(jurisdictionCodec),
  retrievedAt: required(dateCodec),
  sourceUpdatedAt: optional(dateCodec),
  effectiveFrom: optional(dateCodec),
  effectiveUntil: optional(dateCodec),
});

export const regulationCodec = structCodec<Regulation>({
  id: required(stringCodec),
  title: required(stringCodec),
  summary: required(stringCodec),
  jurisdiction: required(jurisdictionCodec),
  scope: required(regulationScopeCodec),
  effect: required(regulationEffectCodec),
  seasonalWindow: optional(seasonalWindowCodec),
  conditions: required(arrayCodec(regulationConditionCodec)),
  source: required(regulationSourceCodec),
  confidence: required(confidenceCodec),
  offlineCachedAt: optional(dateCodec),
  isSynthetic: required(boolCodec),
  provenance: required(dataProvenanceCodec),
});

export const regulatoryCoverageCodec = structCodec<RegulatoryCoverage>({
  id: required(stringCodec),
  name: required(stringCodec),
  countryCode: required(countryCodeCodec),
  polygons: required(polygonsCodec),
  levels: required(arrayCodec(jurisdictionLevelCodec)),
  asOf: required(dateCodec),
  isSynthetic: required(boolCodec),
  provenance: required(dataProvenanceCodec),
});

export const protectedAreaCodec = structCodec<ProtectedArea>({
  id: required(stringCodec),
  name: required(stringCodec),
  kind: required(protectedAreaKindCodec),
  polygons: required(polygonsCodec),
  countryCode: required(countryCodeCodec),
  jurisdiction: required(jurisdictionCodec),
  overnightRule: required(overnightRuleCodec),
  accessRule: required(accessRuleCodec),
  seasonalWindow: optional(seasonalWindowCodec),
  regulationIDs: required(stringsCodec),
  isSynthetic: required(boolCodec),
  provenance: required(dataProvenanceCodec),
  confidence: required(confidenceCodec),
});

// MARK: - Terrain

export const terrainSampleCodec = structCodec<TerrainSample>({
  coordinate: required(geoCoordinateCodec),
  elevation: required(doubleCodec),
  slopeDegrees: required(doubleCodec),
  aspectDegrees: optional(doubleCodec),
  landCover: required(landCoverCodec),
  windExposure: required(windExposureCodec),
  shelteredFromDegrees: optional(doubleCodec),
  distanceToWaterway: optional(doubleCodec),
  distanceToRoad: optional(doubleCodec),
  distanceToSettlement: optional(doubleCodec),
  hazards: required(arrayCodec(terrainHazardCodec)),
});

export const terrainGridCodec = structCodec<TerrainGrid>({
  cellSizeMeters: required(doubleCodec),
  samples: required(arrayCodec(terrainSampleCodec)),
  provenance: required(dataProvenanceCodec),
});

// MARK: - Short bivouac trips (lenient, BivouacTrip.swift)

export const bivouacAreaCodec = structCodec<BivouacArea>({
  centre: required(geoCoordinateCodec),
  radiusMeters: required(doubleCodec),
  elevation: optional(doubleCodec),
  municipality: optional(stringCodec),
  subdivision: optional(stringCodec),
});

export const transitStopCodec = structCodec<TransitStop>({
  name: required(stringCodec),
  coordinate: required(geoCoordinateCodec),
  lines: defaulted(stringsCodec, () => []),
  seasonal: optional(stringCodec),
});

export const tripAccessCodec = structCodec<TripAccess>({
  start: required(transitStopCodec),
  end: required(transitStopCodec),
});

export const officialLayerCheckCodec = structCodec<OfficialLayerCheck>({
  layer: required(stringCodec),
  title: required(stringCodec),
  result: required(layerCheckResultCodec),
  distanceMeters: optional(doubleCodec),
  detail: optional(stringCodec),
  source: required(stringCodec),
  checkedAt: required(dateCodec),
});

export const localRuleNoteCodec = structCodec<LocalRuleNote>({
  level: required(jurisdictionLevelCodec),
  summary: required(stringCodec),
  source: required(stringCodec),
  url: optional(urlCodec),
  confidence: required(confidenceCodec),
  checkedAt: required(dateCodec),
});

export const bivouacTripInfoCodec = structCodec<BivouacTripInfo>({
  area: required(bivouacAreaCodec),
  access: required(tripAccessCodec),
  // Missing = not verified = false (the validator rejects it).
  liftFree: defaulted(boolCodec, () => false),
  nights: defaulted(intCodec, () => 1),
  checks: defaulted(arrayCodec(officialLayerCheckCodec), () => []),
  localRules: defaulted(arrayCodec(localRuleNoteCodec), () => []),
  // Unknown values never become more permissive than "unknown".
  rulesState: lenientDefaulted(regulatoryStateCodec, () => 'unknown'),
  rulesConfidence: lenientOptional(confidenceCodec),
  water: optional(stringCodec),
  fallback: optional(stringCodec),
  caveats: defaulted(stringsCodec, () => []),
  checkedAt: required(dateCodec),
});

// MARK: - Catalogue

export const dayRangeCodec = structCodec<DayRange>({ min: required(intCodec), max: required(intCodec) });

export const permitRequirementCodec = structCodec<PermitRequirement>({
  kind: required(permitKindCodec),
  title: required(stringCodec),
  summary: required(stringCodec),
  authority: optional(stringCodec),
  url: optional(urlCodec),
  confidence: required(confidenceCodec),
  lastChecked: required(dateCodec),
});

export const trailCatalogEntryCodec = structCodec<TrailCatalogEntry>({
  id: required(stringCodec),
  name: required(stringCodec),
  shortName: optional(stringCodec),
  localNames: required(dictionaryCodec(stringCodec)),
  category: required(trailCategoryCodec),
  region: required(trailCatalogRegionCodec),
  countries: required(arrayCodec(countryCodeCodec)),
  subdivisions: required(stringsCodec),
  isLoop: required(boolCodec),
  direction: optional(stringCodec),
  lengthMeters: required(doubleCodec),
  ascentMeters: optional(doubleCodec),
  descentMeters: optional(doubleCodec),
  publishedLengthMeters: optional(doubleCodec),
  typicalDays: optional(dayRangeCodec),
  season: optional(seasonalWindowCodec),
  difficulty: required(difficultyGradeCodec),
  sacScale: optional(sacScaleCodec),
  viaFerrataGrade: optional(stringCodec),
  highestPoint: optional(placeReferenceCodec),
  start: required(placeReferenceCodec),
  finish: required(placeReferenceCodec),
  bounds: required(geoBoundsCodec),
  stageCount: required(intCodec),
  summary: optional(stringCodec),
  markings: optional(stringCodec),
  permits: required(arrayCodec(permitRequirementCodec)),
  officialReference: optional(externalReferenceCodec),
  packResource: optional(stringCodec),
  packBytes: optional(intCodec),
  dataQuality: required(stageDataQualityCodec),
  geometrySource: required(stringCodec),
  provenance: required(arrayCodec(dataProvenanceCodec)),
  isSynthetic: required(boolCodec),
  bivouacTrip: optional(bivouacTripInfoCodec),
});

export const trailCatalogIndexCodec = structCodec<TrailCatalogIndex>({
  formatVersion: required(intCodec),
  generatedAt: required(dateCodec),
  entries: required(arrayCodec(trailCatalogEntryCodec)),
});

// MARK: - Datasets

export const datasetMetadataCodec = structCodec<DatasetMetadata>({
  id: required(stringCodec),
  name: required(stringCodec),
  version: required(stringCodec),
  generatedAt: required(dateCodec),
  isSynthetic: required(boolCodec),
  notice: optional(stringCodec),
  provenance: required(arrayCodec(dataProvenanceCodec)),
});

export const trailDatasetCodec = structCodec<TrailDataset>({
  metadata: required(datasetMetadataCodec),
  trails: required(arrayCodec(trailCodec)),
  stages: required(arrayCodec(trailStageCodec)),
  pointsOfInterest: required(arrayCodec(pointOfInterestCodec)),
  protectedAreas: required(arrayCodec(protectedAreaCodec)),
  regulations: required(arrayCodec(regulationCodec)),
  regulatoryCoverage: required(arrayCodec(regulatoryCoverageCodec)),
  terrain: optional(terrainGridCodec),
});

// MARK: - Trail packs

/** The pack exactly as stored (POIs still compact). */
export interface RawTrailPack extends Omit<TrailPack, 'pointsOfInterest'> {
  pointsOfInterest: CompactPOI[];
}

export const rawTrailPackCodec = structCodec<RawTrailPack>({
  formatVersion: required(intCodec),
  entry: required(trailCatalogEntryCodec),
  trail: required(trailCodec),
  stages: required(arrayCodec(trailStageCodec)),
  provenance: required(arrayCodec(dataProvenanceCodec)),
  pointsOfInterest: required(arrayCodec(compactPOICodec)),
  protectedAreas: required(arrayCodec(protectedAreaCodec)),
  regulations: required(arrayCodec(regulationCodec)),
  regulatoryCoverage: required(arrayCodec(regulatoryCoverageCodec)),
  bivouacTrip: optional(bivouacTripInfoCodec),
});

/** Provenance used for a compact POI whose index is out of range (Swift `TrailPack.dataset()` fallback). */
export function packFallbackProvenance(provenance: readonly DataProvenance[], entryName: string): DataProvenance {
  return (
    provenance[0] ?? {
      provider: 'Unknown',
      dataset: entryName,
      fetchedAt: 0,
      isSynthetic: false,
      licenseReviewStatus: 'pendingHumanReview',
    }
  );
}

/** Expands compact POIs: each gets its full provenance record (shared object) from the pack's table. */
export function expandCompactPOIs(
  pois: readonly CompactPOI[],
  provenance: readonly DataProvenance[],
  entryName: string,
): PointOfInterest[] {
  const fallback = packFallbackProvenance(provenance, entryName);
  return pois.map((c) => {
    const poi: PointOfInterest = {
      id: c.id,
      name: c.n,
      category: c.c,
      coordinate: { latitude: c.la, longitude: c.lo },
      attributes: c.a ?? {},
      provenance: Number.isInteger(c.p) && c.p >= 0 && c.p < provenance.length ? provenance[c.p] : fallback,
      confidence: c.cf,
    };
    if (c.e !== undefined) poi.elevation = c.e;
    if (c.cc !== undefined) poi.countryCode = c.cc;
    return poi;
  });
}

// MARK: - Entry points

/** `catalog.json` → `TrailCatalogIndex`. Throws `DecodingError`. */
export function decodeCatalogIndex(json: unknown): TrailCatalogIndex {
  return trailCatalogIndexCodec.decode(json);
}

/** One catalogue entry. Throws `DecodingError`. */
export function decodeCatalogEntry(json: unknown): TrailCatalogEntry {
  return trailCatalogEntryCodec.decode(json);
}

/** `trails/<id>.json` → `TrailPack` with its POIs expanded (see `packToDataset`). Throws `DecodingError`. */
export function decodeTrailPack(json: unknown): TrailPack {
  const raw = rawTrailPackCodec.decode(json);
  let pointsOfInterest: PointOfInterest[];
  try {
    pointsOfInterest = expandCompactPOIs(raw.pointsOfInterest, raw.provenance, raw.entry.name);
  } catch (e) {
    return rethrowAt(e, 'pointsOfInterest');
  }
  const pack: TrailPack = { ...raw, pointsOfInterest };
  return pack;
}

/** Port of Swift `TrailPack.dataset()`: the pack as a regular dataset. */
export function packToDataset(pack: TrailPack): TrailDataset {
  const fallback = packFallbackProvenance(pack.provenance, pack.entry.name);
  const fetched = pack.entry.provenance.map((p) => p.fetchedAt);
  return {
    metadata: {
      id: `pack:${pack.entry.id}`,
      name: pack.entry.name,
      version: `${pack.formatVersion}`,
      generatedAt: fetched.length > 0 ? Math.max(...fetched) : fallback.fetchedAt,
      isSynthetic: pack.entry.isSynthetic,
      provenance: [...pack.entry.provenance, ...pack.provenance],
    },
    trails: [pack.trail],
    stages: pack.stages,
    pointsOfInterest: pack.pointsOfInterest,
    protectedAreas: pack.protectedAreas,
    regulations: pack.regulations,
    regulatoryCoverage: pack.regulatoryCoverage,
  };
}

/** A `TrailDataset` JSON (world_regulations.json, persisted user imports). Throws `DecodingError`. */
export function decodeTrailDataset(json: unknown): TrailDataset {
  return trailDatasetCodec.decode(json);
}

/**
 * Encodes a dataset as the JSON value Swift's `JSONCoding.makeEncoder()` would write (verbose, lossless geometry
 * unless `compactGeometry`). `decodeTrailDataset(encodeTrailDataset(d))` equals `d`.
 */
export function encodeTrailDataset(dataset: TrailDataset, options: EncodeOptions = {}): unknown {
  return trailDatasetCodec.encode(dataset, options);
}
